import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Shield, Mail, CalendarIcon, ChevronDown, ChevronUp, Check } from 'lucide-react';
import {
  type PrepaymentPolicy,
  resolvePrepaymentPolicy,
  getPrepaymentExpectationText,
} from '@/lib/booking/prepayment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { GuestSelector } from '@/components/accommodation/GuestSelector';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { format, differenceInDays, addDays, isBefore, isSameDay, startOfDay } from 'date-fns';
import { sv } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  parsePriceTextToOre,
  getStayPrice,
  type PriceRule,
} from '@/lib/accommodation/pricing';
import { useTranslate } from '@/i18n';

// --- Booking mode helpers ---
export type BookingMode = 'required' | 'optional' | 'none';

export function resolveBookingMode(raw: string | null | undefined): BookingMode {
  if (raw === 'optional' || raw === 'none') return raw;
  return 'required';
}

/**
 * Locale-aware copy for the three accommodation booking modes.
 * Replaces the previous `getBookingModeCopy(mode)` — see
 * docs/SoT/localization.md §4. Returns the same shape so call-sites
 * only need to rename the function.
 */
export function useBookingModeCopy(mode: BookingMode) {
  const t = useTranslate();
  switch (mode) {
    case 'none':
      return {
        heading: t('accommodation.bookingMode.none.heading'),
        subtitle: t('accommodation.bookingMode.none.subtitle'),
        cta: t('accommodation.bookingMode.none.cta'),
        ctaShort: t('accommodation.bookingMode.none.ctaShort'),
        ctaLoading: t('accommodation.bookingMode.none.ctaLoading'),
        trustLine: null as string | null,
      };
    case 'optional':
      return {
        heading: t('accommodation.bookingMode.optional.heading'),
        subtitle: t('accommodation.bookingMode.optional.subtitle'),
        cta: t('accommodation.bookingMode.optional.cta'),
        ctaShort: t('accommodation.bookingMode.optional.ctaShort'),
        ctaLoading: t('accommodation.bookingMode.optional.ctaLoading'),
        trustLine: t('accommodation.bookingMode.optional.trustLine') as string | null,
      };
    case 'required':
    default:
      return {
        heading: t('accommodation.bookingMode.required.heading'),
        subtitle: t('accommodation.bookingMode.required.subtitle'),
        cta: t('accommodation.bookingMode.required.cta'),
        ctaShort: t('accommodation.bookingMode.required.ctaShort'),
        ctaLoading: t('accommodation.bookingMode.required.ctaLoading'),
        trustLine: t('accommodation.bookingMode.required.trustLine') as string | null,
      };
  }
}

interface BookingFormState {
  name: string;
  email: string;
  phone: string;
  guests: number;
  message: string;
}

export interface AccommodationBookingFormProps {
  accommodationId: string;
  accommodationTitle: string;
  bookingMode: BookingMode;
  guestCapacity: number | null;
  pricePerNightText: string | null;
  bookingDeadlineHours: number;
  priceRules: PriceRule[];
  blocks: { start_date: string; end_date: string }[];
  prepaymentPolicy?: PrepaymentPolicy;
  onSuccess: (formData: { email: string; checkIn: Date; checkOut: Date; nights: number; totalKr: number | null }) => void;
}

// Derive current progression step from form values
type BookingStep = 'dates' | 'guests' | 'contact';

function deriveStep(dateRange: DateRange | undefined, nights: number): BookingStep {
  if (!dateRange?.from || !dateRange?.to || nights < 1) return 'dates';
  return 'guests'; // guests + contact are revealed together once dates are set
}

export function AccommodationBookingForm({
  accommodationId,
  accommodationTitle,
  bookingMode,
  guestCapacity,
  pricePerNightText,
  bookingDeadlineHours,
  priceRules,
  blocks,
  prepaymentPolicy = 'none',
  onSuccess,
}: AccommodationBookingFormProps) {
  const modeCopy = useBookingModeCopy(bookingMode);
  const paymentExpectation = getPrepaymentExpectationText(prepaymentPolicy);

  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [form, setForm] = useState<BookingFormState>({ name: '', email: '', phone: '', guests: 1, message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const nameManuallyEdited = useRef(false);
  const emailManuallyEdited = useRef(false);
  const [prefilled, setPrefilled] = useState(false);

  // Ref for smooth scroll to next section
  const guestSectionRef = useRef<HTMLDivElement>(null);

  // Prefill from authenticated user
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const user = data?.user;
      if (!user) return;
      setForm(prev => {
        const next = { ...prev };
        let didPrefill = false;
        if (!prev.email && user.email && !emailManuallyEdited.current) {
          next.email = user.email;
          didPrefill = true;
        }
        const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
        if (!prev.name && fullName && !nameManuallyEdited.current) {
          next.name = fullName;
          didPrefill = true;
        }
        if (didPrefill) setPrefilled(true);
        return next;
      });
    });
  }, []);

  const checkIn = dateRange?.from;
  const checkOut = dateRange?.to;
  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;
  const basePriceOre = parsePriceTextToOre(pricePerNightText);

  const stayPrice = useMemo(() => {
    if (!checkIn || !checkOut || nights < 1 || !basePriceOre) return null;
    return getStayPrice(
      format(checkIn, 'yyyy-MM-dd'),
      format(checkOut, 'yyyy-MM-dd'),
      basePriceOre,
      priceRules,
    );
  }, [checkIn, checkOut, nights, basePriceOre, priceRules]);

  const estimatedTotalKr = stayPrice ? Math.round(stayPrice.total_ore / 100) : null;
  const avgNightKr = stayPrice && nights > 0 ? Math.round(stayPrice.total_ore / nights / 100) : null;

  const today = useMemo(() => startOfDay(new Date()), []);

  const blockedDateSet = useMemo(() => {
    const set = new Set<string>();
    for (const b of blocks) {
      let current = new Date(b.start_date + 'T00:00:00');
      const end = new Date(b.end_date + 'T00:00:00');
      while (isBefore(current, end)) {
        set.add(format(current, 'yyyy-MM-dd'));
        current = addDays(current, 1);
      }
    }
    return set;
  }, [blocks]);

  const isDateBlocked = useCallback((date: Date): boolean => {
    return blockedDateSet.has(format(date, 'yyyy-MM-dd'));
  }, [blockedDateSet]);

  const handleRangeSelect = useCallback((range: DateRange | undefined) => {
    if (!range?.from) {
      setDateRange(undefined);
      return;
    }
    if (range.from && range.to) {
      let d = addDays(range.from, 1);
      while (isBefore(d, range.to) || isSameDay(d, range.to)) {
        if (isDateBlocked(d)) {
          setDateRange({ from: range.to, to: undefined });
          return;
        }
        d = addDays(d, 1);
      }
    }
    setDateRange(range);
    if (range.from && range.to) {
      setTimeout(() => {
        setCalendarOpen(false);
        // Smooth scroll to the next section after dates are picked
        setTimeout(() => {
          guestSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }, 200);
    }
  }, [isDateBlocked]);

  const earliestCheckIn = useMemo(() => {
    const cutoff = new Date();
    cutoff.setHours(cutoff.getHours() + bookingDeadlineHours);
    return startOfDay(cutoff);
  }, [bookingDeadlineHours]);

  const disabledDays = useCallback((date: Date) => {
    if (isBefore(date, today)) return true;
    if (isBefore(date, earliestCheckIn)) return true;
    return isDateBlocked(date);
  }, [today, earliestCheckIn, isDateBlocked]);

  const dateLabel = useMemo(() => {
    if (!checkIn) return null;
    if (!checkOut) return format(checkIn, 'd MMM', { locale: sv }) + ' → …';
    return `${format(checkIn, 'd MMM', { locale: sv })} – ${format(checkOut, 'd MMM yyyy', { locale: sv })}`;
  }, [checkIn, checkOut]);

  const step = deriveStep(dateRange, nights);
  const hasDates = step !== 'dates';
  const canSubmit = !submitting && hasDates && form.name.trim() && form.email.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkIn || !checkOut) return;
    if (nights < 1) { toast.error('Välj minst en natt'); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error('Fyll i namn och e-post'); return; }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-accommodation-booking', {
        body: {
          accommodation_id: accommodationId,
          check_in: format(checkIn, 'yyyy-MM-dd'),
          check_out: format(checkOut, 'yyyy-MM-dd'),
          guests: form.guests,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: form.message.trim() || null,
        },
      });

      if (error) throw error;
      if (!data?.success) {
        const errorKey = data?.error_key || '';
        const userMsg =
          errorKey === 'deadline_passed'
            ? 'Det är för sent att boka de valda datumen. Välj ett senare datum.'
            : errorKey === 'dates_unavailable'
            ? 'De valda datumen är inte längre tillgängliga.'
            : errorKey === 'duplicate_booking'
            ? 'Du har redan en bokning för dessa datum.'
            : data?.error || 'Bokningen kunde inte genomföras';
        toast.error(userMsg);
        return;
      }

      toast.success(bookingMode === 'optional' ? 'Bokningen är bekräftad!' : 'Förfrågan skickad!');
      onSuccess({ email: form.email, checkIn, checkOut, nights, totalKr: estimatedTotalKr });
    } catch (err: any) {
      toast.error(err?.message || 'Något gick fel');
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic CTA text
  const ctaText = submitting
    ? modeCopy.ctaLoading
    : !hasDates
    ? 'Välj datum ovan'
    : modeCopy.cta;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header + trust */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">{modeCopy.subtitle}</p>

        {/* Prepayment expectation — contextual info row */}
        <div className="flex items-start gap-2 text-sm text-muted-foreground/80">
          <Shield className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="leading-relaxed">{paymentExpectation}</span>
        </div>

        {modeCopy.trustLine && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground/80">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="leading-relaxed">{modeCopy.trustLine}</span>
          </div>
        )}
      </div>

      <Separator />

      {/* ── Step 1: Dates ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <StepIndicator done={hasDates} number={1} />
          <p className="text-sm font-medium text-foreground">Vistelse</p>
        </div>

        <div>
          <Label className="sr-only">Välj datum</Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateRange?.from && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="h-4 w-4 mr-2 shrink-0" />
                {dateLabel || 'Välj in- och utcheckningsdatum'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start" side="bottom">
              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={handleRangeSelect}
                disabled={disabledDays}
                numberOfMonths={2}
                fromDate={today}
                locale={sv}
                className="p-3 pointer-events-auto"
              />
              <p className="text-xs text-muted-foreground px-4 pb-3">
                Grå dagar går inte att boka.
              </p>
            </PopoverContent>
          </Popover>
        </div>

        {/* Stay summary — appears immediately after date selection */}
        {hasDates && (
          <div className="bg-muted/30 rounded-lg px-4 py-3 space-y-2 animate-in fade-in-0 slide-in-from-top-2 duration-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                {format(checkIn!, 'd MMM', { locale: sv })} – {format(checkOut!, 'd MMM', { locale: sv })}
              </span>
              <span className="text-muted-foreground">
                {nights} {nights === 1 ? 'natt' : 'nätter'}
              </span>
            </div>

            {estimatedTotalKr && avgNightKr && (
              <>
                <Separator className="my-1" />
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>{nights} {nights === 1 ? 'natt' : 'nätter'} × {avgNightKr.toLocaleString('sv-SE')} kr</span>
                    <span>{estimatedTotalKr.toLocaleString('sv-SE')} kr</span>
                  </div>
                  <div className="flex justify-between font-semibold text-foreground pt-1">
                    <span>Totalt</span>
                    <span>{estimatedTotalKr.toLocaleString('sv-SE')} kr</span>
                  </div>
                </div>
                {stayPrice?.has_mixed_pricing && (
                  <p className="text-xs text-muted-foreground">
                    Dina datum har blandad prissättning (säsong/special)
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Step 2: Guests — revealed after dates ── */}
      <div
        ref={guestSectionRef}
        className={cn(
          "space-y-3 transition-all duration-300",
          !hasDates && "opacity-40 pointer-events-none select-none"
        )}
        aria-hidden={!hasDates}
      >
        <Separator />
        <div className="flex items-center gap-2">
          <StepIndicator done={hasDates && form.guests >= 1} number={2} />
          <p className="text-sm font-medium text-foreground">Gäster</p>
        </div>
        <div>
          <GuestSelector
            value={form.guests}
            maxGuests={guestCapacity || 10}
            onChange={g => setForm(prev => ({ ...prev, guests: g }))}
            disabled={!hasDates}
          />
          {guestCapacity && (
            <p className="text-xs text-muted-foreground mt-2">Max {guestCapacity} {guestCapacity === 1 ? 'gäst' : 'gäster'}</p>
          )}
        </div>
      </div>

      {/* ── Step 3: Contact — revealed after dates ── */}
      <div
        className={cn(
          "space-y-4 transition-all duration-300",
          !hasDates && "opacity-40 pointer-events-none select-none"
        )}
        aria-hidden={!hasDates}
      >
        <Separator />
        <div className="flex items-center gap-2">
          <StepIndicator done={!!(form.name.trim() && form.email.trim())} number={3} />
          <p className="text-sm font-medium text-foreground">Dina uppgifter</p>
        </div>

        <div>
          <Label htmlFor="b-name">Namn *</Label>
          <Input
            id="b-name"
            value={form.name}
            onChange={e => { nameManuallyEdited.current = true; setForm(prev => ({ ...prev, name: e.target.value })); }}
            placeholder="Ditt namn"
            required
            disabled={!hasDates}
            tabIndex={!hasDates ? -1 : undefined}
            className="mt-1"
          />
          {prefilled && !nameManuallyEdited.current && form.name && (
            <p className="text-xs text-muted-foreground mt-1">Hämtat från ditt konto</p>
          )}
        </div>
        <div>
          <Label htmlFor="b-email">E-post *</Label>
          <Input
            id="b-email"
            type="email"
            value={form.email}
            onChange={e => { emailManuallyEdited.current = true; setForm(prev => ({ ...prev, email: e.target.value })); }}
            placeholder="din@email.se"
            required
            disabled={!hasDates}
            tabIndex={!hasDates ? -1 : undefined}
            className="mt-1"
          />
          {prefilled && !emailManuallyEdited.current && form.email && (
            <p className="text-xs text-muted-foreground mt-1">Hämtat från ditt konto</p>
          )}
        </div>

        {/* Optional fields — collapsible */}
        <div>
          <button
            type="button"
            onClick={() => setShowOptionalFields(v => !v)}
            disabled={!hasDates}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showOptionalFields ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Fler uppgifter (valfritt)
          </button>
          {showOptionalFields && (
            <div className="space-y-4 mt-3 animate-in fade-in-0 slide-in-from-top-1 duration-150">
              <div>
                <Label htmlFor="b-phone" className="text-muted-foreground">Telefon</Label>
                <Input id="b-phone" value={form.phone} onChange={e => setForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Valfritt" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="b-message" className="text-muted-foreground">Meddelande till värden</Label>
                <Textarea id="b-message" value={form.message} onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))} rows={3} placeholder="Berätta gärna vad ni planerar" className="mt-1" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="space-y-3 pt-3">
        <Button type="submit" disabled={!canSubmit} className="w-full" size="lg">
          {ctaText}
        </Button>
        {hasDates && (
          <p className="text-center text-xs text-muted-foreground/70">
            {paymentExpectation}
          </p>
        )}
      </div>
    </form>
  );
}

// Small step indicator dot
function StepIndicator({ done, number }: { done: boolean; number: number }) {
  return (
    <div
      className={cn(
        "flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold shrink-0 transition-colors duration-200",
        done
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground"
      )}
    >
      {done ? <Check className="h-3 w-3" /> : number}
    </div>
  );
}
